import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ViewIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import Paper from "@mui/material/Paper";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { useSnackbar } from "notistack";
import Lottie from "react-lottie";
import RocketImage from "../../../assets/empty.json";
import LoadingImage from "../../../assets/loading.json";
import { SystemDialog } from "../../../utils/Dialog";
import { getReadableDate } from "../../../utils/commons";
import {
  cloneTransformationWorkout,
  deleteTransformationWod,
  fetchPastTransformationWods,
} from "../../../services/TransformationWodService";
import { TransformationWOD } from "./types";
import { TransformationWODView } from "./TransformationWODView";

const emptyOptions = {
  loop: true,
  autoplay: true,
  animationData: RocketImage,
};

const loadingOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingImage,
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[800],
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface IViewTransformationWods {
  onEditWodSelected: (wod: TransformationWOD) => void;
  onDataChanged?: () => void;
  refreshCounter?: number;
  assetBaseUrl?: string;
  assetLoading?: boolean;
}

const ViewTransformationWODs = ({
  onEditWodSelected,
  onDataChanged,
  refreshCounter,
  assetBaseUrl,
  assetLoading,
}: IViewTransformationWods) => {
  const [wods, setWods] = useState<TransformationWOD[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentWod, setCurrentWod] = useState<TransformationWOD>();
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData>>();
  const [hasMore, setHasMore] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const getPastWorkouts = () => {
    setLoading(true);

    fetchPastTransformationWods()
      .then((result) => {
        setWods(result.wods);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const loadMoreWorkouts = () => {
    if (!lastDoc || loadingMore) {
      return;
    }

    setLoadingMore(true);
    fetchPastTransformationWods(lastDoc)
      .then((result) => {
        setWods((prev) => [...prev, ...result.wods]);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
        setLoadingMore(false);
      })
      .catch(() => {
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    getPastWorkouts();
  }, [refreshCounter]);

  const handleDeleteConfirm = () => {
    deleteTransformationWod(currentWod?.date)
      .then(() => {
        setShowDeleteConfirmDialog(false);
        enqueueSnackbar("Transformation WOD deleted successfully!");
        setCurrentWod(undefined);
        getPastWorkouts();
        onDataChanged?.();
      })
      .catch(() => {
        setShowDeleteConfirmDialog(false);
      });
  };

  const handleCloneConfirm = () => {
    if (!currentWod) {
      return;
    }

    cloneTransformationWorkout(currentWod)
      .then(() => {
        enqueueSnackbar("Transformation WOD saved successfully!");
        setShowCloneDialog(false);
        getPastWorkouts();
        onDataChanged?.();
      })
      .catch(() => {
        setShowCloneDialog(false);
        enqueueSnackbar("Failed to clone Transformation WOD", {
          variant: "error",
        });
      });
  };

  const EmptyData = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
        }}
      >
        <Lottie options={emptyOptions} height={120} width={120} />
        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 2, color: "text.secondary" }}
        >
          No transformation workouts found. Create one to get started!
        </Typography>
      </Box>
    );
  };

  const Loading = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <Lottie options={loadingOptions} height={200} width={200} />
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "text.secondary" }}
        >
          Loading transformation workouts...
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "row",
        gap: 2,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {wods.length === 0 || loading ? (
          loading ? (
            <Loading />
          ) : (
            <EmptyData />
          )
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 0,
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: 8,
                height: 8,
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "action.hover",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "text.disabled",
                borderRadius: 4,
                "&:hover": {
                  bgcolor: "text.secondary",
                },
              },
            }}
          >
            <Table stickyHeader aria-label="transformation workouts table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wods.map((wod) => (
                  <StyledTableRow
                    key={wod.date}
                    onClick={() => setCurrentWod(wod)}
                    sx={{
                      cursor: "pointer",
                      ...(currentWod?.date === wod.date && {
                        backgroundColor: "action.selected",
                      }),
                    }}
                  >
                    <StyledTableCell component="th" scope="row">
                      <Typography variant="body2">
                        {getReadableDate(wod.date).toString()}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Tooltip title="View" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentWod(wod);
                          }}
                          size="small"
                          sx={{ color: "text.secondary" }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditWodSelected(wod);
                          }}
                          size="small"
                          sx={{ color: "text.secondary", mx: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentWod(wod);
                            setShowDeleteConfirmDialog(true);
                          }}
                          size="small"
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              color: "error.main",
                              bgcolor: "rgba(244,67,54,0.08)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {hasMore && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1.5,
              mb: 1,
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={loadMoreWorkouts}
              disabled={loadingMore}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {currentWod ? (
          <TransformationWODView
            wod={currentWod}
            assetBaseUrl={assetBaseUrl}
            assetLoading={assetLoading}
            onDeleteWod={() => setShowDeleteConfirmDialog(true)}
            onCloneClicked={() => setShowCloneDialog(true)}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Select a transformation workout to view details
            </Typography>
          </Box>
        )}
      </Box>

      <SystemDialog
        component={
          <Typography variant="overline">
            Are you sure you want to delete this transformation workout?
          </Typography>
        }
        open={showDeleteConfirmDialog}
        handleClose={() => setShowDeleteConfirmDialog(false)}
        handleConfirm={handleDeleteConfirm}
        heading="Delete Transformation Workout"
      />

      <SystemDialog
        component={
          <Typography variant="overline">
            Do you want to use this transformation workout for today?
          </Typography>
        }
        open={showCloneDialog}
        handleClose={() => setShowCloneDialog(false)}
        handleConfirm={handleCloneConfirm}
        heading="Copy Transformation Workout"
      />
    </Box>
  );
};

export default ViewTransformationWODs;
